import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import DataGrid from '~components/DataGrid/DataGrid';
import DataGridPager from '~components/DataGrid/DataGridPager';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { useDataGridSearch } from '~hooks/useDataGridSearch/useDataGridSearch';
import {
  CommissionPayeeType,
  PayCommissionDefault,
} from '~hooks/usePayCommissionDefaults/models';
import usePayCommissionDefaults from '~hooks/usePayCommissionDefaults/usePayCommissionDefaults';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import CommissionDefaultDialog from './CommissionDefaultDialog';
import { commissionRateTypeLabel } from './commissionDefaultSchema';

const PAGE_LIMIT = 25;

type CommissionDefaultDialogState = {
  commissionDefault?: PayCommissionDefault;
  isOpen: boolean;
};

const CommissionDefaultsContent = observer(function CommissionDefaultsContent({
  companyId,
}: {
  companyId: string;
}) {
  const { userStore, toasterStore } = useStores();
  const { destroy, fetchPage } = usePayCommissionDefaults();
  const { searchValue, setSearchQueryValue } = useDataGridSearch();
  const [payeeType, setPayeeType] = useState<CommissionPayeeType>('vendor');
  const isDriver = payeeType === 'driver';

  const [commissionDefaultFormDialogState, setCommissionDefaultFormDialogState] =
    useState<CommissionDefaultDialogState>({ isOpen: false });

  const [deleteCommissionDefaultDialogState, setDeleteCommissionDefaultDialogState] =
    useState<CommissionDefaultDialogState & { isSubmitting: boolean }>({
      isOpen: false,
      isSubmitting: false,
    });

  const { goToPage, isFetching, pagination, reloadCurrentPage, rows, startOver } =
    useCursorPagination<PayCommissionDefault>({
      fetchPage: (params) =>
        fetchPage({ ...params, companyId, payeeType, search: searchValue }),
      limit: PAGE_LIMIT,
    });

  useEffect(() => {
    void startOver();
  }, [payeeType, searchValue, startOver]);

  const userPermissions = userStore.getPermissions();

  const handleChangeTab = useCallback(
    (_event: React.SyntheticEvent, next: CommissionPayeeType) => {
      setPayeeType(next);
      setSearchQueryValue('');
    },
    [setSearchQueryValue],
  );

  const handleCreateNewCommissionDefaultClick = useCallback(() => {
    setCommissionDefaultFormDialogState({ isOpen: true });
  }, []);

  const handleDeleteCommissionDefaultClick = useCallback(
    (commissionDefault: PayCommissionDefault) => {
      setDeleteCommissionDefaultDialogState({
        isOpen: true,
        isSubmitting: false,
        commissionDefault,
      });
    },
    [],
  );

  const handleCloseCommissionDefaultFormDialog = useCallback(() => {
    setCommissionDefaultFormDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const isEditingCommissionDefault = Boolean(
    commissionDefaultFormDialogState.commissionDefault,
  );

  const handleCommissionDefaultSaved = useCallback(() => {
    void (isEditingCommissionDefault ? reloadCurrentPage() : startOver());
  }, [isEditingCommissionDefault, reloadCurrentPage, startOver]);

  const handleCloseDeleteCommissionDefaultDialog = useCallback(() => {
    setDeleteCommissionDefaultDialogState((state) =>
      state.isSubmitting ? state : { ...state, isOpen: false },
    );
  }, []);

  const deleteCommissionDefault = useCallback(async () => {
    const commissionDefaultId = deleteCommissionDefaultDialogState.commissionDefault?.id;

    if (!commissionDefaultId) {
      return;
    }

    try {
      setDeleteCommissionDefaultDialogState((state) => ({
        ...state,
        isSubmitting: true,
      }));

      await destroy(companyId, commissionDefaultId);

      toasterStore.push(alert(t('pay_commission_defaults.deleted'), AlertTypes.success));
      setDeleteCommissionDefaultDialogState({ isOpen: false, isSubmitting: false });
      void reloadCurrentPage();
    } catch {
      setDeleteCommissionDefaultDialogState((state) => ({
        ...state,
        isSubmitting: false,
      }));
    }
  }, [
    companyId,
    deleteCommissionDefaultDialogState.commissionDefault?.id,
    destroy,
    reloadCurrentPage,
  ]);

  const columns = useMemo(
    () =>
      [
        {
          field: 'payeeName',
          headerName: t('form_fields.name'),
          flex: 1,
          sortable: false,
        },
        {
          field: 'rateType',
          headerName: t('form_fields.rate_type'),
          flex: 1,
          sortable: false,
          valueFormatter: (value: PayCommissionDefault['rateType']) =>
            commissionRateTypeLabel(value),
        },
        {
          field: 'percentage',
          headerName: t('pay_commission.percentage_label'),
          flex: 1,
          sortable: false,
          valueFormatter: (value: PayCommissionDefault['percentage']) => `${value}%`,
        },
        {
          field: 'actions',
          align: 'left',
          headerAlign: 'left',
          headerName: t('actions.actions'),
          type: 'actions',
          width: 96,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          hideable: false,
          display: 'flex' as const,
          renderCell: (params: GridRenderCellParams) => {
            const options = userPermissions.canDeleteRate
              ? [
                  {
                    title: t('actions.delete'),
                    icon: <Delete />,
                    callBack: () => handleDeleteCommissionDefaultClick(params.row),
                  },
                ]
              : [];
            return (
              <>
                {userPermissions.canEditRate && (
                  <IconButton
                    size="small"
                    aria-label={t('actions.edit')}
                    onClick={() => {
                      setCommissionDefaultFormDialogState({
                        isOpen: true,
                        commissionDefault: params.row,
                      });
                    }}
                  >
                    <Edit />
                  </IconButton>
                )}
                {options.length > 0 && (
                  <SimpleMenu options={options}>
                    <MoreHoriz />
                  </SimpleMenu>
                )}
              </>
            );
          },
        },
      ] as GridColDef[],
    [
      handleDeleteCommissionDefaultClick,
      userPermissions.canDeleteRate,
      userPermissions.canEditRate,
    ],
  );

  const isGridBusy = isFetching || deleteCommissionDefaultDialogState.isSubmitting;

  return (
    <>
      <SettingsPageHeader
        title={t('pay_commission_defaults.title')}
        description={t('pay_commission_defaults.description')}
      />
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t('pay_commission_defaults.example')}
      </Typography>
      <Tabs onChange={handleChangeTab} value={payeeType} sx={{ mb: 2 }}>
        <Tab label={t('pay_commission_defaults.vendors')} value="vendor" />
        <Tab label={t('pay_commission_defaults.internal_drivers')} value="driver" />
      </Tabs>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          disabled={!userPermissions.canCreateRate}
          onClick={handleCreateNewCommissionDefaultClick}
          startIcon={<Add />}
        >
          {t('pay_commission_defaults.create')}
        </SmallButton>
      </Box>
      <Box data-test-id="pay-commission-defaults-data-grid">
        <DataGrid
          key={payeeType}
          id={`pay-commission-defaults-${payeeType}-data-grid`}
          columns={columns}
          rows={rows}
          loading={isGridBusy}
          filterMode="server"
          disableColumnFilter
          onChangeFilter={setSearchQueryValue}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isGridBusy}
              onNavigate={(link) => void goToPage(link)}
              pagination={pagination}
            />
          }
        />
      </Box>
      <Typography variant="body2" sx={{ mt: 2 }}>
        {t('pay_commission_defaults.missing_rate_type_hint')}
      </Typography>
      <CommissionDefaultDialog
        commissionDefault={commissionDefaultFormDialogState.commissionDefault}
        companyId={companyId}
        isDriver={isDriver}
        isOpen={commissionDefaultFormDialogState.isOpen}
        onClose={handleCloseCommissionDefaultFormDialog}
        onSuccess={handleCommissionDefaultSaved}
      />
      <ConfirmationDialog
        isBusy={deleteCommissionDefaultDialogState.isSubmitting}
        isOpen={deleteCommissionDefaultDialogState.isOpen}
        onClose={handleCloseDeleteCommissionDefaultDialog}
        onConfirm={deleteCommissionDefault}
        title={t('pay_commission_defaults.delete')}
      >
        <Typography>{t('pay_commission_defaults.delete_hint')}</Typography>
      </ConfirmationDialog>
    </>
  );
});

const CommissionDefaults = observer(function CommissionDefaults() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  return <CommissionDefaultsContent companyId={companyId} key={companyId} />;
});

export default CommissionDefaults;
